<template>
    <Teleport to="body">
        <div v-if="open" class="pb2-cmdk" @pointerdown.self="close">
            <div
                ref="panelEl"
                class="pb2-cmdk__panel"
                role="dialog"
                aria-modal="true"
                :aria-label="t('command_palette')"
                @keydown="trap.onKeydown"
            >
                <div class="pb2-cmdk__search">
                    <AdminText
                        ref="inputEl"
                        v-model="query"
                        type="search"
                        size="lg"
                        role="combobox"
                        aria-autocomplete="list"
                        aria-expanded="true"
                        aria-controls="pb2-cmdk-list"
                        :aria-activedescendant="flat.length ? `pb2-cmdk-option-${activeIndex}` : undefined"
                        :placeholder="t('command_placeholder')"
                        @keydown="onKeydown"
                    />
                </div>

                <p v-if="!flat.length" class="pb2-cmdk__empty">
                    {{ t('command_no_results') }}
                </p>

                <ul v-else id="pb2-cmdk-list" class="pb2-cmdk__list" role="listbox">
                    <template v-for="(item, index) in flat" :key="item.id">
                        <li
                            v-if="index === 0 || flat[index - 1].group !== item.group"
                            class="pb2-cmdk__group"
                            role="presentation"
                        >
                            {{ groupTitle(item.group) }}
                        </li>
                        <li>
                            <div
                                :id="`pb2-cmdk-option-${index}`"
                                class="pb2-cmdk__item"
                                :class="{
                                    'pb2-cmdk__item--active': index === activeIndex,
                                    'pb2-cmdk__item--dimmed': !item.enabled,
                                }"
                                role="option"
                                :title="item.enabled ? undefined : item.disabledHint"
                                :aria-selected="index === activeIndex"
                                :aria-disabled="item.enabled ? undefined : true"
                                @pointerenter="activeIndex = index"
                                @click="run(item)"
                            >
                                <span
                                    v-if="item.group === 'insert' || item.group === 'shared'"
                                    class="pb2-cmdk__icon"
                                    aria-hidden="true"
                                >
                                    <load-file v-if="item.image" replace-with :src="item.image"></load-file>
                                    <Icon v-else icon="phosphor/regular/squares-four" class="size-3.5" />
                                </span>
                                <span class="pb2-cmdk__label">{{ item.label }}</span>
                                <span v-if="item.hint" class="pb2-cmdk__hint">{{ item.hint }}</span>
                            </div>
                        </li>
                    </template>
                </ul>
            </div>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useBuilderT } from '@src/Common/Builder/host';
import { createFocusTrap } from '@src/Common/Builder/focusTrap';
import { rankSearch } from '@src/Common/Builder/paletteSearch';
import { markerId } from '@src/Common/Builder/types';
import type { BlockNode } from '@src/Common/Builder/types';
import { useBlockInsertion } from '@src/Common/Builder/useBlockInsertion';
import { usePaletteHistory } from '@src/Common/Builder/usePaletteHistory';
import { useBuilderBridge } from '@src/Common/Builder/useBuilderBridge';
import { useBuilderRegistry } from '@src/Common/Builder/useBuilderRegistry';
import { useBuilderStore } from '@src/Common/Builder/useBuilderStore';
import { injectDraftAutosaveOptional } from '@src/Common/Builder/useDraftAutosave';
import { usePanelManagerOptional } from '@src/Common/Builder/usePanelManager';

const t = useBuilderT();

type CommandGroup = 'action' | 'insert' | 'shared' | 'navigate';

interface CommandItem {
    id: string;
    group: CommandGroup;
    label: string;
    hint?: string;
    /** Type icon URL for insert/shared rows; null = fallback glyph. */
    image?: string | null;
    enabled: boolean;
    disabledHint?: string;
    run(): void;
}

const open = defineModel<boolean>({ required: true });
const keymapOpen = defineModel<boolean>('keymap', { default: false });

const store = useBuilderStore();
const bridge = useBuilderBridge();
const registry = useBuilderRegistry();
const insertion = useBlockInsertion();
const history = usePaletteHistory();
const autosave = injectDraftAutosaveOptional();
const panels = usePanelManagerOptional();

const query = ref('');
const activeIndex = ref(0);
const inputEl = ref<{ focus: () => void } | null>(null);
const panelEl = ref<HTMLElement | null>(null);
const trap = createFocusTrap();

const editable = computed(() => store.mode.value === 'edit');

const close = (): void => {
    open.value = false;
};

watch(open, async (value) => {
    if (value) {
        query.value = '';
        activeIndex.value = 0;
        await nextTick();

        if (panelEl.value) {
            trap.activate(panelEl.value);
        }

        inputEl.value?.focus();
    } else {
        trap.deactivate();
    }
});

// Breakpoint switching is a view operation — available in readonly too.
const breakpointItems = (): CommandItem[] =>
    (['desktop', 'tablet', 'mobile'] as const).map((breakpoint) => ({
        id: `action:breakpoint:${breakpoint}`,
        group: 'action' as const,
        label: t('action_breakpoint', {
            breakpoint: t(`breakpoint_${breakpoint}`),
        }),
        enabled: store.breakpoint.value !== breakpoint,
        run: () => {
            store.breakpoint.value = breakpoint;
        },
    }));

const actionItems = computed<CommandItem[]>(() => {
    if (!editable.value) {
        return breakpointItems();
    }

    // Canvas-lite has no working copy — publish is a draft-only action
    const publishItems: CommandItem[] = autosave
        ? [
              {
                  id: 'action:publish',
                  group: 'action',
                  label: t('publish_changes'),
                  enabled: true,
                  run: () => void autosave.publish(),
              },
          ]
        : [];

    return [
        ...breakpointItems(),
        ...publishItems,
        {
            id: 'action:undo',
            group: 'action',
            label: t('undo'),
            enabled: store.canUndo.value,
            run: () => void store.undo(),
        },
        {
            id: 'action:redo',
            group: 'action',
            label: t('redo'),
            enabled: store.canRedo.value,
            run: () => void store.redo(),
        },
        {
            id: 'action:close-panel',
            group: 'action',
            label: t('action_close_panel'),
            enabled: store.selectedId.value !== null,
            run: () => store.select(null),
        },
        {
            id: 'action:keymap',
            group: 'action',
            label: t('action_keymap'),
            enabled: true,
            run: () => {
                keymapOpen.value = true;
            },
        },
        ...panelItems(),
    ];
});

// Floating panel toggles (F2) — absent in Canvas-lite (no panel manager).
const panelItems = (): CommandItem[] => {
    if (!panels) {
        return [];
    }

    const entries: Array<{ id: string; label: string }> = [
        { id: 'palette', label: t('tab_blocks') },
        { id: 'outline', label: t('outline') },
        { id: 'block', label: t('block_settings') },
    ];

    return entries.map((panel) => ({
        id: `action:panel:${panel.id}`,
        group: 'action' as const,
        label: t('action_toggle_panel', { panel: panel.label }),
        enabled: true,
        run: () => panels.toggle(panel.id),
    }));
};

const insertItems = computed<CommandItem[]>(() => {
    if (!editable.value) {
        return [];
    }

    return registry.types.value.map((type) => ({
        id: `insert:${type.slug}`,
        group: 'insert' as const,
        label: type.name,
        hint: type.slug,
        image: type.image ?? null,
        enabled: insertion.canInsertType(type.slug),
        disabledHint: t('columns_needs_section'),
        run: () => void insertion.insertTypeBlock(type.slug),
    }));
});

const sharedItems = computed<CommandItem[]>(() => {
    if (!editable.value) {
        return [];
    }

    return registry.blocks.value.map((block) => ({
        id: `shared:${String(block.id)}`,
        group: 'shared' as const,
        label: block.name,
        enabled: true,
        run: () => void insertion.insertSharedBlock(block.id),
    }));
});

const navigateItems = computed<CommandItem[]>(() => {
    const items: CommandItem[] = [];

    const visit = (blocks: BlockNode[]): void => {
        for (const block of blocks) {
            const id = markerId(block);

            if (id !== null) {
                items.push({
                    id: `navigate:${id}`,
                    group: 'navigate',
                    label: store.labels.value[id] ?? String(block.type),
                    hint: String(block.type),
                    enabled: true,
                    run: () => {
                        store.select(id);
                        bridge.scrollTo(id);
                    },
                });
            }

            if (block.children?.length) {
                visit(block.children);
            }
        }
    };

    visit(store.content.value);

    return items;
});

/** Empty query: actions + favorites/recent inserts. A query searches everything. */
const flat = computed<CommandItem[]>(() => {
    const trimmed = query.value.trim();

    if (trimmed === '') {
        const inserts = [...insertItems.value, ...sharedItems.value];
        const entryId = (entry: { kind: string; key: string }): string =>
            entry.kind === 'type' ? `insert:${entry.key}` : `shared:${entry.key}`;
        const pinned = [...history.favorites.value, ...history.recent.value]
            .map((entry) => inserts.find((item) => item.id === entryId(entry)))
            .filter((item): item is CommandItem => item !== undefined);
        const seen = new Set<string>();
        const unique = pinned.filter((item) => !seen.has(item.id) && seen.add(item.id) !== undefined);

        return [...actionItems.value, ...unique];
    }

    const order: CommandGroup[] = ['action', 'insert', 'shared', 'navigate'];

    return order.flatMap((group) => {
        const pool = { action: actionItems, insert: insertItems, shared: sharedItems, navigate: navigateItems }[
            group
        ].value;

        return rankSearch(trimmed, pool);
    });
});

watch(flat, () => {
    activeIndex.value = 0;
});

const groupTitle = (group: CommandGroup): string => {
    const keys: Record<CommandGroup, string> = {
        action: 'group_actions',
        insert: 'group_insert',
        shared: 'group_shared',
        navigate: 'group_navigate',
    };

    return t(`${keys[group]}`);
};

const run = (item: CommandItem): void => {
    if (!item.enabled) {
        return;
    }

    close();
    item.run();
};

const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
        event.preventDefault();
        close();
    } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        activeIndex.value = flat.value.length ? (activeIndex.value + 1) % flat.value.length : 0;
    } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        activeIndex.value = flat.value.length ? (activeIndex.value - 1 + flat.value.length) % flat.value.length : 0;
    } else if (event.key === 'Enter') {
        event.preventDefault();
        const item = flat.value[activeIndex.value];

        if (item) {
            run(item);
        }
    }
};
</script>

<style scoped>
.pb2-cmdk {
    position: fixed;
    inset: 0;
    z-index: 2147483646;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding-top: 18vh;
    background: rgb(0 0 0 / 0.35);
}

.pb2-cmdk__panel {
    display: flex;
    flex-direction: column;
    width: min(34rem, calc(100vw - 2rem));
    max-height: 60vh;
    overflow: hidden;
    background: var(--pb2-surface-raised, #fff);
    border: 1px solid var(--pb2-border, #e4e4e7);
    border-radius: var(--pb2-radius, 6px);
    box-shadow: var(--pb2-shadow, 0 10px 40px rgb(0 0 0 / 0.2));
}

/* Search row hosts the global <AdminText> (inherits dark-aware .forms__input,
   fixing the light-input-in-dark-mode bug). */
.pb2-cmdk__search {
    padding: 0.625rem;
    border-bottom: 1px solid var(--pb2-border, #e4e4e7);
}

.pb2-cmdk__search :deep(.forms__input) {
    width: 100%;
}

.pb2-cmdk__empty {
    margin: 0;
    padding: 0.75rem 1rem;
    font-size: 0.8125rem;
    color: var(--pb2-muted, #a1a1aa);
}

.pb2-cmdk__list {
    margin: 0;
    padding: 0.375rem;
    overflow-y: auto;
    list-style: none;
}

.pb2-cmdk__group {
    padding: 0.375rem 0.625rem 0.125rem;
    font-size: 0.625rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--pb2-muted, #a1a1aa);
}

.pb2-cmdk__icon {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    overflow: hidden;
    color: var(--pb2-muted, #a1a1aa);
}

.pb2-cmdk__icon load-file {
    display: inline-flex;
    width: 14px;
    height: 14px;
}

.pb2-cmdk__icon svg {
    width: 100%;
    height: 100%;
}

.pb2-cmdk__item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.375rem 0.625rem;
    font-size: 0.8125rem;
    text-align: left;
    color: var(--pb2-text, #3f3f46);
    background: none;
    border: 0;
    border-radius: var(--pb2-radius, 6px);
    cursor: pointer;
}

.pb2-cmdk__item--active {
    background: var(--pb2-surface, #fafafa);
}

.pb2-cmdk__item--dimmed {
    opacity: 0.4;
}

.pb2-cmdk__label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.pb2-cmdk__hint {
    font-size: 0.6875rem;
    color: var(--pb2-muted, #a1a1aa);
}
</style>
